import { Component, ElementRef, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { ErrorMessageService } from 'src/app/services/error-message.service';
import { EtraceService } from 'src/app/services/etrace.service';
import { MessageModel } from '../../models/message-model';

@Component({
  selector: 'ness-manual-ftsid',
  templateUrl: './manual-ftsid.component.html'
})
export class ManualFTSIDComponent implements OnInit {
  firearmTrace: {
    RowLabel: string,
    ETraceValue: string,
    NESSValue: string,
    Match: boolean
  }[] = [];
  loading: boolean = false;
  matchForm: FormGroup = this.fb.group({
    FTSID: ['', [Validators.required, Validators.pattern('^[T|t].*')]],
    FirearmID: [this.config.data['firearmId'].toString(), Validators.required]
  });

  private _firearmTraceMatchModel: ServerModels.FirearmETraceMatchViewModel = null;
  private _invalidTraceData: boolean = true;
  public get invalidTraceData(): boolean {
    return this._invalidTraceData;
  }

  constructor(public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    public msgService: ErrorMessageService,
    private etraceService: EtraceService,
    private hostElement: ElementRef) { }

  ngOnInit() {
    this.msgService.clear();
    this.matchForm.get('FTSID').reset();
    this.firearmTrace = [];
    this.loading = false;
    this._invalidTraceData = true;
  }

  public onSearch(): void {
    this.msgService.clear();
    if (this.matchForm.valid) {
      const formModel = this.matchForm.value;
      this.firearmTrace = [];
      this._firearmTraceMatchModel = null;
      this.loading = true;
      this.etraceService.matchFirearmETrace(formModel.FTSID, formModel.FirearmID).pipe(take(1)).subscribe({
        next: (data: ServerModels.FirearmETraceMatchViewModel[]) => {
          this.loading = false;
          if (!data.length) {
            return;
          }
          if (data[0].ETrace && !!data[0].ETrace.ETraceID.length) {
            this._firearmTraceMatchModel = data[0];
            this.firearmTrace = [...[{
              RowLabel: 'Firearm Type',
              ETraceValue: data[0].ETrace?.Type || '',
              NESSValue: data[0].NIBINFirearmType,
              Match: data[0].IsTypeMatch
            }, {
              RowLabel: 'Manufacturer',
              ETraceValue: data[0].ETrace?.Manufacturer || '',
              NESSValue: data[0].NIBINManufacturer,
              Match: data[0].IsManufacturerMatch
            }, {
              RowLabel: 'Model',
              ETraceValue: data[0].ETrace?.Model || '',
              NESSValue: data[0].NIBINFirearmModel,
              Match: null
            }, {
              RowLabel: 'Caliber',
              ETraceValue: data[0].ETrace?.Caliber || '',
              NESSValue: data[0].NIBINCaliber,
              Match: data[0].IsCaliberMatch
            },
            {
              RowLabel: 'Serial Number',
              ETraceValue: data[0].ETrace?.SerialNumber || '',
              NESSValue: data[0].NIBINSerialNumber,
              Match: data[0].IsSerialNumberMatch
            }]];
            this._invalidTraceData = (+data[0].IsCaliberMatch) + (+data[0].IsManufacturerMatch) + (+data[0].IsSerialNumberMatch) + (+data[0].IsTypeMatch) < 2;
          } else {
            let messageModel: MessageModel = {
              alertClass: 'alert-warning',
              iconClass: 'fas fa-exclamation-circle',
              headerText: 'FTS ID already exists.',
              messageText: null
            } as MessageModel;

            this.msgService.setMessage(messageModel);
          }
        },
        complete: () => this.loading = false
      });
    }
  }

  public onConfirmMatch(): void {
    if (this.matchForm.invalid)
      return;

    let nibinFirearmETrace = {
      FirearmETraceID: null,
      FirearmID: this.config.data['firearmId'].toString(),
      ETraceID: null
    } as ServerModels.ENESS_XREF_NIBINFirearmsETrace;
    let enessETrace = {
      ETraceID: null,
      FTS_ID: this._firearmTraceMatchModel.ETrace.ETraceID,
      FTS_Seq_Number: this._firearmTraceMatchModel.ETrace.FTS_Seq_Number,
      FTSTypeID: this._firearmTraceMatchModel.FTSTypeID,
      PurchaseDate: this._firearmTraceMatchModel.ETrace.PurchaseDate,
      PurchaseCity: this._firearmTraceMatchModel.ETrace.PurchaseCity,
      PurchaseState: this._firearmTraceMatchModel.ETrace.PurchaseState,
      PurchaseZip: this._firearmTraceMatchModel.ETrace.PurchaseZip,
      FFLNumber: this._firearmTraceMatchModel.ETrace.FFLNumber,
      FFLName: this._firearmTraceMatchModel.ETrace.FFLName,
      FFLAddress: this._firearmTraceMatchModel.ETrace.FFLAddress,
      ENESS_XREF_NIBINFirearmsETrace: [nibinFirearmETrace]
    } as ServerModels.ENESS_ETrace;
    this.etraceService.confirmManualFTSID(enessETrace).subscribe({
      next: (result: number) => {
        this.resetTraceSearch();
        let messageModel: MessageModel = {
          alertClass: 'alert-success',
          iconClass: 'fas fa-check-circle',
          headerText: 'New Trace FTS ID added.',
          messageText: null
        } as MessageModel;

        this.msgService.setMessage(messageModel);
      }
    });
  }

  public resetTraceSearch(): void {
    this.msgService.clear();
    this.matchForm.get('FTSID').reset();
    this.loading = false;
    this._invalidTraceData = true;
    this.firearmTrace = [];
  }
}
